import { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
    faList,
    faPuzzlePiece,
    faStar,
    faTennisBall,
    faHotel,
    faListCheck,
    faCar,
    faVanShuttle,
    faBadgeDollar,
    faWhistle,
    faDogLeashed
} from '@fortawesome/pro-light-svg-icons';
import { PageSidebarItem } from 'components/PageSidebar/PageSidebarModel';
import { useServiceCategories } from 'hooks/useServiceCategories';
import { canSetupEvaluation } from 'hooks/useServiceCategoryAccess';
import {
    BoardingPath,
    DaycarePath,
    DiscountsPath,
    MembershipsPath,
    ServicesPath,
    TransportationPath,
    TrainingPath,
    WalkingPath
} from 'routes/Paths';
import { membershipMarketplaceIds } from 'utils/MarketplaceConfig';
import store from 'store';
import { isDiscountsEnabled } from './DiscountsUtils';
import { usePermissions } from 'hooks/usePermissions';

/**
 * ===================================
 * TYPES & INTERFACES
 * ===================================
 */

/**
 * Configuration for static sidebar items
 * Use when you have a fixed list of sidebar items
 */
export interface StaticSidebarConfig {
    type: 'static';
    items: PageSidebarItem[];
    defaultKey: string;
    navigation?: Record<string, string>; // key -> path
}

/**
 * Configuration for dynamic sidebar items
 * Use when sidebar items are generated at runtime
 */
export interface DynamicSidebarConfig {
    type: 'dynamic';
    getItems: () => PageSidebarItem[];
    defaultKey: string;
    navigation?: Record<string, string>;
}

/**
 * Configuration for shared sidebar items
 * Use when multiple pages share the same sidebar structure
 */
export interface SharedSidebarConfig {
    type: 'shared';
    configName: string; // Reference to shared configuration
    defaultKey: string;
}

/** Internal type for resolved configurations */
interface ResolvedSidebarConfig {
    items: PageSidebarItem[];
    navigation?: Record<string, string>; // key -> path
}

export type SidebarConfig = StaticSidebarConfig | DynamicSidebarConfig | SharedSidebarConfig;

/**
 * ===================================
 * SHARED CONFIGURATIONS
 * ===================================
 */

/** Services sidebar item definitions */
const SERVICES_SIDEBAR_ITEMS = {
    PRIMARY: {
        icon: faList,
        title: 'Grooming',
        key: 'primary'
    },
    DAYCARE: {
        icon: faTennisBall,
        title: 'Daycare',
        key: 'daycare'
    },
    BOARDING: {
        icon: faHotel,
        title: 'Boarding',
        key: 'boarding'
    },
    TRANSPORTATION: {
        icon: faVanShuttle,
        title: 'Transportation',
        key: 'transportation'
    },
    TRAINING: {
        icon: faWhistle,
        title: 'Training',
        key: 'training'
    },
    WALKING: {
        icon: faDogLeashed,
        title: 'Walking',
        key: 'walking'
    },
    ADD_ONS: {
        icon: faPuzzlePiece,
        title: 'Add-Ons',
        key: 'add_ons'
    },
    MEMBERSHIPS: {
        icon: faStar,
        title: 'Memberships',
        key: 'memberships',
        exclusiveMarketplaceIds: membershipMarketplaceIds()
    },
    EVALUATION: {
        icon: faListCheck,
        title: 'Evaluation',
        key: 'evaluation'
    },
    DISCOUNTS: {
        icon: faBadgeDollar,
        title: 'Automatic Discounts',
        key: 'discounts'
    }
} as const;

/** Services navigation mapping */
const SERVICES_NAVIGATION = {
    primary: ServicesPath,
    add_ons: `${ServicesPath}#add_ons`,
    memberships: MembershipsPath,
    daycare: DaycarePath,
    boarding: BoardingPath,
    transportation: TransportationPath,
    training: TrainingPath,
    walking: WalkingPath,
    evaluation: `${ServicesPath}#evaluation`,
    discounts: DiscountsPath
} as const;

/** Shared configurations registry */
const SHARED_CONFIGS = {
    SERVICES: {
        items: Object.values(SERVICES_SIDEBAR_ITEMS) as PageSidebarItem[],
        navigation: SERVICES_NAVIGATION
    }
} as const;

export type SharedConfigName = keyof typeof SHARED_CONFIGS;

/**
 * ===================================
 * HELPER FUNCTIONS
 * ===================================
 */

/**
 * Resolves the sidebar configuration based on type
 */
function resolveSidebarConfig(config: SidebarConfig): ResolvedSidebarConfig {
    switch (config.type) {
        case 'shared':
            return SHARED_CONFIGS[config.configName as SharedConfigName];
        case 'static':
            return { items: config.items, navigation: config.navigation };
        case 'dynamic':
            return { items: config.getItems(), navigation: config.navigation };
        default:
            throw new Error(`Unsupported sidebar config type: ${(config as any).type}`);
    }
}

/**
 * Determines which navigation method to use based on target path
 */
function getNavigationMethod(targetPath: string, currentPath: string): 'replace' | 'push' {
    // Use replace for hash-only navigation within the same page
    if (targetPath.includes('#') && targetPath.startsWith(currentPath)) {
        return 'replace';
    }
    // Use push for navigation to different pages
    return 'push';
}

/**
 * Finds the appropriate sidebar key based on current location
 */
function findKeyFromLocation(
    currentPath: string,
    hash: string,
    navigation: Record<string, string>,
    items: PageSidebarItem[],
    defaultKey: string
): string {
    const currentFullPath = currentPath + (hash ? `#${hash}` : '');

    // 1. Try to match exact path + hash
    const exactMatch = Object.entries(navigation).find(([, path]) => path === currentFullPath);

    if (exactMatch) {
        return exactMatch[0];
    }

    // 2. If we have a hash that matches an item key, use it
    if (hash && items.some(item => item.key === hash)) {
        return hash;
    }

    // 3. Try to match path only (without hash)
    const pathOnlyMatch = Object.entries(navigation).find(([, path]) => path === currentPath);

    if (pathOnlyMatch) {
        return pathOnlyMatch[0];
    }

    // 4. Default fallback
    return defaultKey;
}

/**
 * ===================================
 * MAIN HOOK
 * ===================================
 */

/**
 * Main hook for managing sidebar navigation
 *
 * @param config - The sidebar configuration
 * @returns Object with selectedKey, setSelectedKey function, and sidebarItems
 *
 * @example Static Configuration - Custom sidebar for a specific page
 * ```tsx
 * // For a Settings page with custom sidebar items
 * const { selectedKey, setSelectedKey, sidebarItems } = usePageSidebar({
 *   type: 'static',
 *   items: [
 *     { icon: faUser, title: 'Profile', key: 'profile' },
 *     { icon: faBell, title: 'Notifications', key: 'notifications' },
 *     { icon: faLock, title: 'Security', key: 'security' }
 *   ],
 *   defaultKey: 'profile',
 *   navigation: {
 *     'profile': '/settings',
 *     'notifications': '/settings#notifications',
 *     'security': '/settings/security'
 *   }
 * });
 * ```
 *
 * @example Dynamic Configuration - Runtime-generated sidebar
 * ```tsx
 * // For a page where sidebar items depend on user permissions or data
 * const { selectedKey, setSelectedKey, sidebarItems } = usePageSidebar({
 *   type: 'dynamic',
 *   getItems: () => {
 *     const items = [{ icon: faHome, title: 'Dashboard', key: 'dashboard' }];
 *     if (userHasPermission('admin')) {
 *       items.push({ icon: faUsers, title: 'Users', key: 'users' });
 *     }
 *     if (userHasFeature('reports')) {
 *       items.push({ icon: faChart, title: 'Reports', key: 'reports' });
 *     }
 *     return items;
 *   },
 *   defaultKey: 'dashboard',
 *   navigation: {
 *     'dashboard': '/admin',
 *     'users': '/admin/users',
 *     'reports': '/admin/reports'
 *   }
 * });
 * ```
 *
 * @example Shared Configuration - Reusing predefined sidebar
 * ```tsx
 * // For Services/Memberships/Daycare pages using shared config
 * const { selectedKey, setSelectedKey, sidebarItems } = usePageSidebar({
 *   type: 'shared',
 *   configName: 'SERVICES',
 *   defaultKey: 'primary'
 * });
 * ```
 */
export const usePageSidebar = (config: SidebarConfig) => {
    const history = useHistory();
    const location = useLocation();
    const [selectedKey, setSelectedKey] = useState(config.defaultKey);

    // Resolve configuration once
    const resolvedConfig = useMemo(() => resolveSidebarConfig(config), [config]);

    /**
     * Handles sidebar item selection and navigation
     */
    const handleKeyChange = useCallback(
        (newKey: string) => {
            // Update selected key immediately for UI responsiveness
            setSelectedKey(newKey);

            // Navigate if navigation mapping exists
            if (resolvedConfig.navigation && newKey in resolvedConfig.navigation) {
                const targetPath = resolvedConfig.navigation[newKey]!;
                const currentFullPath = location.pathname + location.hash;

                if (targetPath !== currentFullPath) {
                    const method = getNavigationMethod(targetPath, location.pathname);
                    history[method](targetPath);
                }
            }
        },
        [resolvedConfig.navigation, location, history]
    );

    /**
     * Synchronizes selected key with current browser location
     */
    useEffect(() => {
        if (!resolvedConfig.navigation) return;

        const hash = location.hash.replace('#', '');
        const keyFromLocation = findKeyFromLocation(
            location.pathname,
            hash,
            resolvedConfig.navigation,
            resolvedConfig.items,
            config.defaultKey
        );

        // Update selected key if different (prevents infinite loops)
        if (keyFromLocation !== selectedKey) {
            setSelectedKey(keyFromLocation);
        }
    }, [location.pathname, location.hash, resolvedConfig, config.defaultKey, selectedKey]);

    return {
        selectedKey,
        setSelectedKey: handleKeyChange,
        sidebarItems: resolvedConfig.items
    };
};

/**
 * ===================================
 * CONVENIENCE HOOKS
 * ===================================
 */

/**
 * Convenience hook for Services-related pages (Services, Memberships, Daycare)
 *
 * @param defaultKey - The key that should be selected by default ('primary', 'memberships', 'daycare', etc.)
 * @returns Object with selectedKey, setSelectedKey function, and sidebarItems
 *
 * @example
 * ```tsx
 * // In Services.tsx
 * const { selectedKey, setSelectedKey, sidebarItems } = useServicesNavigation('primary');
 *
 * // In Memberships.tsx
 * const { selectedKey, setSelectedKey, sidebarItems } = useServicesNavigation('memberships');
 *
 * // In Daycare.tsx
 * const { selectedKey, setSelectedKey, sidebarItems } = useServicesNavigation('daycare');
 * ```
 */
export const useServicesNavigation = (defaultKey: string) => {
    return usePageSidebar({
        type: 'shared',
        configName: 'SERVICES',
        defaultKey
    });
};

/**
 * Filtered version of Services navigation that only shows enabled services
 * Filters Daycare and Boarding based on marketplace notification settings
 *
 * @param defaultKey - The key that should be selected by default ('primary', 'memberships', 'daycare', etc.)
 * @returns Object with selectedKey, setSelectedKey function, and filtered sidebarItems
 *
 * @example
 * ```tsx
 * // In Services.tsx - shows only enabled services
 * const { selectedKey, setSelectedKey, sidebarItems } = useFilteredServicesNavigation('primary');
 * ```
 */
export const useFilteredServicesNavigation = (defaultKey: string) => {
    const permissions = usePermissions();
    const categories = useServiceCategories();
    const { hasGrooming, hasDaycare, hasBoarding, hasTransportation, hasTraining, hasWalking } = categories;
    const marketplace = store.getState().marketplace.marketplace;
    const discountsEnabled = isDiscountsEnabled(marketplace.id);
    const membershipsEnabled = permissions.memberships.manage;

    return usePageSidebar({
        type: 'dynamic',
        getItems: () =>
            Object.values(SERVICES_SIDEBAR_ITEMS).filter(item => {
                if (item.key === 'primary') return hasGrooming;
                if (item.key === 'daycare') return hasDaycare;
                if (item.key === 'boarding') return hasBoarding;
                if (item.key === 'transportation') return hasTransportation;
                if (item.key === 'discounts') return discountsEnabled;
                if (item.key === 'training') return hasTraining;
                if (item.key === 'walking') return hasWalking;
                if (item.key === 'evaluation') return canSetupEvaluation(categories);
                if (item.key === 'memberships') return membershipsEnabled;
                return true;
            }),
        defaultKey,
        navigation: SERVICES_NAVIGATION
    });
};

/**
 * ===================================
 * COMPLETE USAGE EXAMPLES
 * ===================================
 */

/* 
Example 1: Clients Page (Based on src/pages/Clients.tsx)
=======================================================

// Current implementation using manual sidebar management
export const Clients: FunctionComponent<ClientsProps> = props => {
    const [selectedKey, setSelectedKey] = useState<string>(ClientsItem.key);
    
    const sidebarItems = useMemo(() => {
        return [ClientsItem, AgreementsItem, MembershipsItem].filter(item => {
            if (item.key === AgreementsItem.key) {
                return agreementsEnabled;
            }
            return true;
        });
    }, [hasReportsPremiumAccess]);

    useEffect(() => {
        switch (location.hash) {
            case '#agreements':
                setSelectedKey(AgreementsItem.key);
                break;
            case '#sold-memberships':
                setSelectedKey(MembershipsItem.key);
                break;
            default:
                setSelectedKey(ClientsItem.key);
        }
    }, [location.hash]);

    // ... rest of component
};

// REFACTORED VERSION using PageSidebarUtils:
export const Clients: FunctionComponent<ClientsProps> = props => {
    const hasReportsPremiumAccess = useHasPremiumAccess('agreements');
    
    const { selectedKey, setSelectedKey, sidebarItems } = usePageSidebar({
        type: 'dynamic',
        getItems: () => {
            const items = [
                { icon: faUser, title: 'Clients', key: 'clients' },
                { icon: faFileSignature, title: 'Agreements', key: 'agreements' },
                { icon: faStars, title: 'Sold Memberships', key: 'memberships', exclusiveMarketplaceIds: membershipMarketplaceIds() }
            ];
            
            return items.filter(item => {
                if (item.key === 'agreements') {
                    return agreementsEnabled;
                }
                return true;
            });
        },
        defaultKey: 'clients',
        navigation: {
            'clients': '/clients',
            'agreements': '/clients#agreements',
            'memberships': '/clients#sold-memberships'
        }
    });

    const sidebar = (
        <PageSidebar
            title="Clients"
            items={sidebarItems}
            selectedKey={selectedKey}
            onSelect={setSelectedKey}
        />
    );

    // Component rendering based on selectedKey...
    switch (selectedKey) {
        case 'agreements':
            return agreements;
        case 'memberships':
            return memberships;
        default:
            return editData === null ? clients : client;
    }
};
*/

/* 
Example 2: Client Profile Page (Based on src/pages/Client.tsx)
============================================================

// Current implementation using manual sidebar management
export const Client: FunctionComponent<ClientProps> = props => {
    const [sidebarKey, setSidebarKey] = useState(props.petId ? `${PrefixPetKey}${props.petId}` : ClientKey);
    
    const sidebarItems: Array<PageSidebarItem> = [
        { icon: faImagesUser, title: 'Client Information', key: ClientKey },
        {
            icon: faPaw,
            title: 'Pets',
            key: PetsKey,
            subitems: [
                ...(pets || []).map(pet => ({
                    title: `${pet.deceased ? '🌈 ' : ''}${pet.name}`,
                    key: `${PrefixPetKey}${pet.id}`
                })),
                {
                    icon: faPlus,
                    title: 'Add Pet',
                    key: AddPetKey,
                    isLink: true
                }
            ]
        },
        ...(clientHasIntakeFormResponses
            ? [{ icon: faMemoCircleCheck, title: 'Intake Form', key: IntakeFormKey }]
            : []),
        { icon: faCreditCard, title: 'Cards', key: CardsKey },
        { icon: faCalendar, title: 'History', key: HistoryKey },
        { icon: faFileSignature, title: 'Agreements', key: AgreementsKey }
    ]
        .filter(item => client?.id !== undefined || item.key === ClientKey)
        .filter(item => (agreementsEnabled && hasReportsPremiumAccess) || item.key !== AgreementsKey);

    const selectKeyHandler = (key: string) => {
        setSidebarKey(key);
        // ... complex logic for handling different keys
    };

    // ... rest of component
};

// REFACTORED VERSION using PageSidebarUtils (for modal/no URL navigation):
export const Client: FunctionComponent<ClientProps> = props => {
    const hasReportsPremiumAccess = useHasPremiumAccess('agreements');
    const clientHasIntakeFormResponses = React.useMemo(() => {
        return (
            (client && client?.customQuestionResponses.length > 0) ||
            pets.some(pet => pet.customQuestionResponses.length > 0)
        );
    }, [client, pets]);
    
    const { selectedKey, setSelectedKey, sidebarItems } = usePageSidebar({
        type: 'dynamic',
        getItems: () => {
            const items = [
                { icon: faImagesUser, title: 'Client Information', key: 'client' },
                {
                    icon: faPaw,
                    title: 'Pets',
                    key: 'pets',
                    subitems: [
                        ...(pets || []).map(pet => ({
                            title: `${pet.deceased ? '🌈 ' : ''}${pet.name}`,
                            key: `pet:${pet.id}`
                        })),
                        {
                            icon: faPlus,
                            title: 'Add Pet',
                            key: 'add_pet',
                            isLink: true
                        }
                    ]
                },
                ...(clientHasIntakeFormResponses
                    ? [{ icon: faMemoCircleCheck, title: 'Intake Form', key: 'intake_form' }]
                    : []),
                { icon: faCreditCard, title: 'Cards', key: 'cards' },
                { icon: faCalendar, title: 'History', key: 'history' },
                { icon: faFileSignature, title: 'Agreements', key: 'agreements' }
            ]
                .filter(item => client?.id !== undefined || item.key === 'client')
                .filter(item => (agreementsEnabled && hasReportsPremiumAccess) || item.key !== 'agreements');
            
            return items;
        },
        defaultKey: props.petId ? `pet:${props.petId}` : 'client'
        // No navigation property = tab-like behavior within modal/page
    });

    const handleSelectKey = useCallback((key: string) => {
        setSelectedKey(key);
        containerRef.current!.scrollTop = 0;
        
        // Handle specific key logic
        switch (key) {
            case 'client':
                setEditPet(null);
                setAddPet(null);
                break;
            case 'add_pet':
                setAddPet(createEmptyPet(uuid, client?.id!));
                setEditPet(null);
                break;
            // ... other cases
        }
    }, [setSelectedKey, client]);

    const sidebar = (
        <PageSidebar
            title={title}
            info={info}
            items={sidebarItems}
            selectedKey={selectedKey}
            onSelect={handleSelectKey}
            onBack={props.onClose}
        />
    );

    // Render different components based on selectedKey...
    return (
        <PrivatePage pageSidebar={sidebar}>
            {selectedKey === 'client' && <ClientComponent />}
            {selectedKey === 'agreements' && <ClientSignatures />}
            {selectedKey === 'cards' && <ClientCards />}
            {selectedKey === 'history' && <BookingsComponentClient />}
            {selectedKey === 'intake_form' && <ClientIntakeForm />}
            {// Pet-specific rendering logic}
        </PrivatePage>
    );
};
*/

/* 
Example 3: Creating a Custom Shared Configuration (Based on Project Patterns)
============================================================================

// Step 1: Add new shared configuration to PageSidebarUtils.ts

const INSIGHTS_SIDEBAR_ITEMS = {
    OVERVIEW: { icon: faChartLine, title: 'Overview', key: 'overview' },
    BOOKINGS: { icon: faCalendar, title: 'Bookings Report', key: 'bookings' },
    CLIENTS: { icon: faUser, title: 'Client Report', key: 'clients' },
    REVENUES: { icon: faDollarSign, title: 'Revenue Report', key: 'revenues' },
    PETS: { icon: faPaw, title: 'Pet Birthday Report', key: 'pets' }
} as const;

const INSIGHTS_NAVIGATION = {
    'overview': '/insights',
    'bookings': '/insights#bookings',
    'clients': '/insights#clients', 
    'revenues': '/insights#revenues',
    'pets': '/insights#pets'
} as const;

// Step 2: Add to SHARED_CONFIGS registry
const SHARED_CONFIGS = {
    SERVICES: { 
        items: Object.values(SERVICES_SIDEBAR_ITEMS),
        navigation: SERVICES_NAVIGATION
    },
    INSIGHTS: {
        items: Object.values(INSIGHTS_SIDEBAR_ITEMS),
        navigation: INSIGHTS_NAVIGATION
    }
} as const;

// Step 3: Create convenience hook
export const useInsightsNavigation = (defaultKey: string) => {
    return usePageSidebar({
        type: 'shared',
        configName: 'INSIGHTS',
        defaultKey
    });
};

// Step 4: Use in your page component
// File: src/pages/Insights.tsx
export const Insights = () => {
    const { selectedKey, setSelectedKey, sidebarItems } = useInsightsNavigation('overview');
    
    const sidebar = (
        <PageSidebar
            title="Insights"
            items={sidebarItems}
            selectedKey={selectedKey}
            onSelect={setSelectedKey}
        />
    );

    return (
        <PrivatePage 
            title="Insights - Cuddles"
            pageName="/insights"
            pageSidebar={sidebar}
        >
            {selectedKey === 'overview' && <InsightsOverview />}
            {selectedKey === 'bookings' && <BookingsInsightsReport />}
            {selectedKey === 'clients' && <ClientInsightsReport />}
            {selectedKey === 'revenues' && <RevenuesReport />}
            {selectedKey === 'pets' && <PetBirthdayReport />}
        </PrivatePage>
    );
};
*/
